import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { loadFontsWhenIdle } from "../lib/load-fonts";
import { CookieConsent } from "@/components/CookieConsent";
import { SignupPopup } from "@/components/SignupPopup";
import { VisitTracker } from "@/components/VisitTracker";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-script text-6xl text-primary">404</h1>
        <h2 className="mt-3 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link to="/" className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong while loading this page. Please try again.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >Try again</button>
          <a href="/" className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent">Go home</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "Kds9qHeD4_quXwcQU3-6pLR2Te2-1GA8I1MEFmf9JjE" },
      { name: "keywords", content: "Bengali singer booking, Sanchayeeta Seal, versatile singer India, live performance singer, voiceover artist, music album singer, Bengali music, event singer booking" },
      { property: "og:site_name", content: "Surer Adda With Sanchayeeta" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#5a1a22" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/UlmYVFzrdxOqdg3qodJ5yNWZ9NW2/social-images/social-1780603530745-1000303195.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/UlmYVFzrdxOqdg3qodJ5yNWZ9NW2/social-images/social-1780603530745-1000303195.webp" },
      { name: "google-adsense-account", content: "ca-pub-6518434900714653" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/UlmYVFzrdxOqdg3qodJ5yNWZ9NW2/social-images/social-1780603530745-1000303195.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/UlmYVFzrdxOqdg3qodJ5yNWZ9NW2/social-images/social-1780603530745-1000303195.webp" },
      { name: "google-adsense-account", content: "ca-pub-6518434900714653" },
    ],
    links: [
      // Same-origin hero is already preloaded by the index route. Warm connections
      // for the image host (Supabase storage / render) and deferred tag hosts so
      // their first byte costs ~0ms when they fire.
      { rel: "preconnect", href: "https://heikajtncwyllimlezjd.supabase.co", crossOrigin: "anonymous" } as any,
      { rel: "dns-prefetch", href: "https://heikajtncwyllimlezjd.supabase.co" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://pagead2.googlesyndication.com" },
      { rel: "preload", as: "style", href: appCss, fetchPriority: "high" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/webp", href: "/site-logo.webp" },
      { rel: "shortcut icon", type: "image/webp", href: "/site-logo.webp" },
      { rel: "apple-touch-icon", href: "/site-logo.webp" },
    ],
    scripts: [
      // Stub dataLayer + consent defaults (denied) so any queued events aren't lost.
      // Actual GTM / gtag / AdSense tags only load AFTER the user saves cookie consent
      // (granted for analytics and/or advertising). Default ON in the banner still
      // requires an explicit Save / Accept click before tags initialize.
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied'});",
      },
      {
        children:
          "(function(){var tagsLoaded=false,adsLoaded=false;function loadTags(){if(tagsLoaded)return;tagsLoaded=true;var g=document.createElement('script');g.async=true;g.src='https://www.googletagmanager.com/gtag/js?id=AW-10868760846';document.head.appendChild(g);gtag('config','AW-10868760846');gtag('config','G-49EXBNHKWL');(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-5DR3VZ6J');}function loadAds(){if(adsLoaded)return;if(!document.querySelector('ins.adsbygoogle'))return;adsLoaded=true;var a=document.createElement('script');a.async=true;a.crossOrigin='anonymous';a.src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6518434900714653';document.head.appendChild(a);}function apply(c){if(!c)return;gtag('consent','update',{ad_storage:c.advertising,ad_user_data:c.advertising,ad_personalization:c.advertising,analytics_storage:c.analytics});var anyGranted=c.analytics==='granted'||c.advertising==='granted';if(anyGranted){if('requestIdleCallback' in window){requestIdleCallback(loadTags,{timeout:2000});}else{setTimeout(loadTags,800);}}if(c.advertising==='granted'){if('requestIdleCallback' in window){requestIdleCallback(loadAds,{timeout:2500});}else{setTimeout(loadAds,1200);}}}try{var raw=localStorage.getItem('cookie-consent-v2');if(raw){var p=JSON.parse(raw);if(p&&(p.analytics==='granted'||p.analytics==='denied')){apply({analytics:p.analytics,advertising:p.advertising==='granted'?'granted':'denied'});}}}catch(e){}window.addEventListener('lovable:consent',function(e){apply(e&&e.detail);});})();",
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head><HeadContent /></head>
      <body>
        {/* GTM <noscript> fallback intentionally omitted: without JS the consent
            banner cannot run, so we cannot honor the user's cookie choice. */}

        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => { loadFontsWhenIdle(); }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthSync />
      <VisitTracker />
      <Outlet />
      <CookieConsent />
      <SignupPopup />
      <Toaster richColors position="top-center" />
    </QueryClientProvider>
  );
}

function AuthSync() {
  const router = useRouter();
  const qc = useQueryClient();
  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;
    const init = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      if (cancelled) return;
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
        if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
        router.invalidate();
        if (event !== "SIGNED_OUT") qc.invalidateQueries();
      });
      unsub = () => subscription.unsubscribe();
    };
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (ric) ric(() => void init(), { timeout: 2000 });
    else setTimeout(() => void init(), 200);
    return () => { cancelled = true; unsub?.(); };
  }, [router, qc]);
  return null;
}
